import React, { useState, useEffect } from 'react';
import { 
    Store, Upload, Image, Save, CheckCircle2, ShieldCheck, 
    Globe, Phone, Mail, Award, AlertCircle, FileText, Share2, Check
} from 'lucide-react';
import { getVendorApplicationStatus, uploadMedia } from '../api/client';
import { useAuth } from '../context/AuthContext';
import { isRTL } from '../utils/translations';
import Spinner from '../components/Spinner';

export default function SellerStore() {
    const rtl = isRTL();
    const { user } = useAuth();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [savedNotice, setSavedNotice] = useState(false);
    const [copiedLink, setCopiedLink] = useState(false);

    const [storeData, setStoreData] = useState({
        businessName: '',
        brandTagline: '',
        bioAr: '',
        bioEn: '',
        supportPhone: '',
        supportEmail: '',
        logoUrl: '',
        bannerUrl: '',
        commercialRegister: '',
        taxNumber: '',
        shippingCoverage: ['SA', 'AE', 'OM', 'QA', 'KW', 'BH', 'SO', 'YE', 'US']
    });

    useEffect(() => {
        const loadStoreData = async () => {
            try {
                const res = await getVendorApplicationStatus();
                if (res && res.data) {
                    const d = res.data;
                    setStoreData(prev => ({
                        ...prev,
                        businessName: d.businessName || user?.fullName || 'LUXVONO Sovereign Merchant',
                        brandTagline: d.tagline || (rtl ? 'منتجات فاخرة بجودة استثنائية ومعتمدة عالمياً' : 'Curated authentic luxury collections'),
                        bioAr: d.bioAr || '',
                        bioEn: d.bioEn || '',
                        supportPhone: d.phone || user?.phoneNumber || '+966 50 123 4567',
                        supportEmail: d.email || user?.email || '',
                        commercialRegister: d.commercialRegister || '',
                        taxNumber: d.taxNumber || ''
                    }));
                }
            } catch (err) {
                console.error('Failed to load store profile:', err);
            } finally {
                setLoading(false);
            }
        };

        loadStoreData();
    }, [user, rtl]);

    const handleLogoUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        try {
            const res = await uploadMedia([file], () => {}, 'ORIGINAL');
            if (res && res.data && res.data[0]) {
                setStoreData(prev => ({ ...prev, logoUrl: res.data[0].url || res.data[0].rawUrl }));
            }
        } catch (err) {
            console.error('Logo upload error:', err);
        }
    };

    const handleBannerUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        try {
            const res = await uploadMedia([file], () => {}, 'ORIGINAL');
            if (res && res.data && res.data[0]) {
                setStoreData(prev => ({ ...prev, bannerUrl: res.data[0].url || res.data[0].rawUrl }));
            }
        } catch (err) {
            console.error('Banner upload error:', err);
        }
    };

    const handleSave = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            // Save to localStorage or vendor profile endpoint
            localStorage.setItem('luxvono_seller_store_profile', JSON.stringify(storeData));
            setSavedNotice(true);
            setTimeout(() => setSavedNotice(false), 4000);
        } catch (err) {
            console.error('Save store profile failed:', err);
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div style={{ padding: '60px 0', textAlign: 'center' }}>
                <Spinner />
            </div>
        );
    }

    return (
        <div style={{ maxWidth: 840, marginInline: 'auto' }}>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 28, flexWrap: 'wrap', gap: 16 }}>
                <div>
                    <h1 style={{ margin: '0 0 6px 0', fontSize: 22, fontWeight: 800, color: 'var(--seller-text, #111827)' }}>
                        {rtl ? 'تخصيص وهوية المتجر الفاخر' : 'Luxury Storefront Profile & Branding'}
                    </h1>
                    <p style={{ margin: 0, fontSize: 13, color: 'var(--seller-muted, #64748b)' }}>
                        {rtl 
                            ? 'إدارة شعار علامتك التجارية، الغلاف البصري، وقنوات خدمة العملاء الموثقة.' 
                            : 'Customize your verified vendor storefront, brand banners, and concierge channels.'}
                    </p>
                </div>

                <button 
                    onClick={handleSave}
                    disabled={saving}
                    style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#FE2C55', color: '#ffffff', padding: '10px 24px', borderRadius: 10, fontWeight: 700, fontSize: 13, border: 'none', cursor: 'pointer', boxShadow: '0 4px 14px rgba(254, 44, 85, 0.35)', transition: 'all 0.2s ease' }}
                >
                    <Save size={16} />
                    <span>{saving ? (rtl ? 'جارٍ الحفظ...' : 'Saving...') : (rtl ? 'حفظ إعدادات المتجر' : 'Save Changes')}</span>
                </button>
            </div>

            {/* Success Alert */}
            {savedNotice && (
                <div style={{ background: 'rgba(16,185,129,0.12)', border: '1px solid rgba(16,185,129,0.3)', borderRadius: 10, padding: '12px 18px', marginBottom: 20, display: 'flex', alignItems: 'center', gap: 10, color: '#059669', fontSize: 13, fontWeight: 600 }}>
                    <CheckCircle2 size={18} />
                    <span>{rtl ? 'تم حفظ كافة بيانات وهيكلية المتجر بنجاح!' : 'Store profile and brand identity updated successfully!'}</span>
                </div>
            )}

            {/* Store Preview Banner Card */}
            <div style={{ background: 'var(--seller-card, #ffffff)', border: '1px solid var(--seller-border, #e5e7eb)', borderRadius: 16, overflow: 'hidden', marginBottom: 28, boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
                {/* Banner Header */}
                <div style={{ height: 180, background: storeData.bannerUrl ? `url(${storeData.bannerUrl}) center/cover` : 'linear-gradient(135deg, #111827 0%, #1f2937 50%, #111827 100%)', position: 'relative' }}>
                    <label style={{ position: 'absolute', bottom: 12, right: 12, background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(8px)', color: '#fff', padding: '6px 14px', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
                        <Upload size={14} />
                        <span>{rtl ? 'تغيير صورة الغلاف' : 'Upload Banner'}</span>
                        <input type="file" accept="image/*" onChange={handleBannerUpload} style={{ display: 'none' }} />
                    </label>
                </div>

                {/* Profile Strip */}
                <div style={{ padding: '0 24px 24px 24px', display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16, marginTop: -40 }}>
                    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 18 }}>
                        <div style={{ width: 84, height: 84, borderRadius: 14, background: '#ffffff', border: '3px solid #ffffff', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
                            {storeData.logoUrl ? (
                                <img src={storeData.logoUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            ) : (
                                <Store size={36} color="#FE2C55" />
                            )}
                            <label style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.5)', opacity: 0, hover: { opacity: 1 }, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', transition: 'opacity 0.2s' }} onMouseEnter={e => e.currentTarget.style.opacity = '1'} onMouseLeave={e => e.currentTarget.style.opacity = '0'}>
                                <Upload size={18} color="#fff" />
                                <input type="file" accept="image/*" onChange={handleLogoUpload} style={{ display: 'none' }} />
                            </label>
                        </div>

                        <div style={{ marginBottom: 4 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <h3 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: 'var(--seller-text, #111827)' }}>
                                    {storeData.businessName || 'LUXVONO Brand'}
                                </h3>
                                <span style={{ background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.25)', color: '#10B981', fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 4, display: 'flex', alignItems: 'center', gap: 4 }}>
                                    <ShieldCheck size={12} />
                                    VERIFIED
                                </span>
                            </div>
                            <p style={{ margin: '4px 0 0 0', fontSize: 13, color: 'var(--seller-muted, #64748b)' }}>
                                {storeData.brandTagline}
                            </p>
                        </div>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <button
                            type="button"
                            onClick={() => {
                                const sellerId = user?.sellerId || user?.username || `SELLER-${user?.id || '700094'}`;
                                const cleanId = String(sellerId).replace(/^@/, '');
                                const url = `${window.location.origin}/@${cleanId}`;
                                if (navigator.clipboard) {
                                    navigator.clipboard.writeText(url);
                                    setCopiedLink(true);
                                    setTimeout(() => setCopiedLink(false), 2500);
                                }
                            }}
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 6,
                                background: '#111827',
                                color: '#ffffff',
                                border: '1px solid #374151',
                                padding: '8px 16px',
                                borderRadius: 8,
                                fontSize: 12,
                                fontWeight: 700,
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                                boxShadow: '0 2px 6px rgba(0,0,0,0.1)'
                            }}
                        >
                            {copiedLink ? <Check size={14} color="#10B981" /> : <Share2 size={14} />}
                            <span>{copiedLink ? (rtl ? 'تم نسخ الرابط!' : 'Link Copied!') : (rtl ? 'نسخ رابط المتجر للعملاء' : 'Copy Store Link')}</span>
                        </button>
                    </div>
                </div>
            </div>

            {/* Profile Form Details */}
            <form onSubmit={handleSave} style={{ background: 'var(--seller-card, #ffffff)', border: '1px solid var(--seller-border, #e5e7eb)', borderRadius: 16, padding: '28px 24px', display: 'flex', flexDirection: 'column', gap: 20, boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
                <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: 'var(--seller-text, #111827)', borderBottom: '1px solid var(--seller-border, #e5e7eb)', paddingBottom: 14 }}>
                    {rtl ? 'المعلومات الرسمية للمتجر' : 'Official Store Information'}
                </h3>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16 }}>
                    <div>
                        <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--seller-text, #374151)', marginBottom: 6 }}>
                            {rtl ? 'اسم العلامة التجارية / المتجر' : 'Brand / Store Name'}
                        </label>
                        <input 
                            type="text" 
                            value={storeData.businessName} 
                            onChange={e => setStoreData({ ...storeData, businessName: e.target.value })} 
                            style={{ width: '100%', padding: '10px 14px', background: 'var(--seller-input-bg, #ffffff)', border: '1px solid var(--seller-input-border, #d1d5db)', borderRadius: 8, color: 'var(--seller-text, #111827)', fontSize: 13, boxSizing: 'border-box' }}
                        />
                    </div>

                    <div>
                        <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--seller-text, #374151)', marginBottom: 6 }}>
                            {rtl ? 'شعار المتجر التسويقي (Slogan)' : 'Brand Slogan'}
                        </label>
                        <input 
                            type="text" 
                            value={storeData.brandTagline} 
                            onChange={e => setStoreData({ ...storeData, brandTagline: e.target.value })} 
                            style={{ width: '100%', padding: '10px 14px', background: 'var(--seller-input-bg, #ffffff)', border: '1px solid var(--seller-input-border, #d1d5db)', borderRadius: 8, color: 'var(--seller-text, #111827)', fontSize: 13, boxSizing: 'border-box' }}
                        />
                    </div>
                </div>

                {/* Multilingual Bio */}
                <div>
                    <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--seller-text, #374151)', marginBottom: 6 }}>
                        {rtl ? 'نبذة عن المتجر (بالعربية)' : 'About Store (Arabic)'}
                    </label>
                    <textarea 
                        rows={3} 
                        value={storeData.bioAr} 
                        onChange={e => setStoreData({ ...storeData, bioAr: e.target.value })} 
                        placeholder={rtl ? 'اكتب نبذة فاخرة عن تاريخ علامتك التجارية، جودة الصنع، وسرعة التوصيل...' : 'Write brand story in Arabic...'}
                        style={{ width: '100%', padding: '10px 14px', background: 'var(--seller-input-bg, #ffffff)', border: '1px solid var(--seller-input-border, #d1d5db)', borderRadius: 8, color: 'var(--seller-text, #111827)', fontSize: 13, boxSizing: 'border-box' }}
                    />
                </div>

                <div>
                    <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--seller-text, #374151)', marginBottom: 6 }}>
                        {rtl ? 'نبذة عن المتجر (بالإنجليزية)' : 'About Store (English)'}
                    </label>
                    <textarea 
                        rows={3} 
                        value={storeData.bioEn} 
                        onChange={e => setStoreData({ ...storeData, bioEn: e.target.value })} 
                        placeholder="Write brand story in English..."
                        style={{ width: '100%', padding: '10px 14px', background: 'var(--seller-input-bg, #ffffff)', border: '1px solid var(--seller-input-border, #d1d5db)', borderRadius: 8, color: 'var(--seller-text, #111827)', fontSize: 13, boxSizing: 'border-box' }}
                    />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16 }}>
                    <div>
                        <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--seller-text, #374151)', marginBottom: 6 }}>
                            {rtl ? 'هاتف / واتساب خدمة العملاء' : 'Customer Concierge Phone'}
                        </label>
                        <input 
                            type="text" 
                            value={storeData.supportPhone} 
                            onChange={e => setStoreData({ ...storeData, supportPhone: e.target.value })} 
                            style={{ width: '100%', padding: '10px 14px', background: 'var(--seller-input-bg, #ffffff)', border: '1px solid var(--seller-input-border, #d1d5db)', borderRadius: 8, color: 'var(--seller-text, #111827)', fontSize: 13, boxSizing: 'border-box' }}
                        />
                    </div>

                    <div>
                        <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--seller-text, #374151)', marginBottom: 6 }}>
                            {rtl ? 'البريد الإلكتروني الرسمي للاستفسارات' : 'Official Inquiries Email'}
                        </label>
                        <input 
                            type="email" 
                            value={storeData.supportEmail} 
                            onChange={e => setStoreData({ ...storeData, supportEmail: e.target.value })} 
                            style={{ width: '100%', padding: '10px 14px', background: 'var(--seller-input-bg, #ffffff)', border: '1px solid var(--seller-input-border, #d1d5db)', borderRadius: 8, color: 'var(--seller-text, #111827)', fontSize: 13, boxSizing: 'border-box' }}
                        />
                    </div>
                </div>
            </form>
        </div>
    );
}
